import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Phone, Lock, ArrowLeft, CheckCircle, Loader2, KeyRound, Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: request OTP, 2: verify OTP, 3: new password
  const [method, setMethod] = useState('email'); // 'email' or 'phone'
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    code: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [resetToken, setResetToken] = useState('');
  const [sentVia, setSentVia] = useState([]);

  const requestOTP = async () => {
    const contact = method === 'email' ? formData.email : formData.phone;
    if (!contact) {
      toast.error(`Inserisci ${method === 'email' ? 'la tua email' : 'il tuo numero di telefono'}`);
      return;
    }

    setLoading(true);
    try {
      const payload = method === 'email' 
        ? { email: formData.email }
        : { phone: formData.phone };
      
      const response = await axios.post(`${API}/auth/request-password-reset`, payload);
      
      setSentVia(response.data.sent_to || [method]);
      toast.success('Codice OTP inviato!');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio del codice');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!formData.code || formData.code.length !== 6) {
      toast.error('Inserisci il codice a 6 cifre');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        code: formData.code,
        ...(method === 'email' ? { email: formData.email } : { phone: formData.phone })
      };
      
      const response = await axios.post(`${API}/auth/verify-password-reset-otp`, payload);
      
      setResetToken(response.data.reset_token);
      toast.success('Codice verificato!');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice non valido');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }
    if (formData.newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        reset_token: resetToken,
        new_password: formData.newPassword
      });
      
      toast.success('Password reimpostata con successo!');
      setTimeout(() => navigate('/auth'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset della password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #F8F7F4 0%, #E8F5E9 100%)'}}>
      <div className="max-w-md w-full mx-4">
        {/* Back Button */}
        <button
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/auth')}
          className="flex items-center gap-2 mb-6 text-sm font-medium hover:underline"
          style={{color: '#2A9FCB'}}
        >
          <ArrowLeft className="w-4 h-4" />
          {step > 1 ? 'Indietro' : 'Torna al login'}
        </button>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8" style={{border: '1px solid rgba(127, 179, 160, 0.2)'}}>
          {/* Logo/Icon */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" 
                 style={{background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}>
              {step === 1 && <KeyRound className="w-8 h-8 text-white" />}
              {step === 2 && <Shield className="w-8 h-8 text-white" />}
              {step === 3 && <Lock className="w-8 h-8 text-white" />}
            </div>
            <h2 className="text-2xl font-bold" style={{color: '#2A9FCB'}}>
              {step === 1 && 'Password dimenticata?'}
              {step === 2 && 'Inserisci il codice'}
              {step === 3 && 'Nuova password'}
            </h2>
            <p className="text-sm mt-2" style={{color: '#2A9FCB', opacity: 0.7}}>
              {step === 1 && 'Inserisci la tua email o il tuo numero di telefono per ricevere un codice di verifica'}
              {step === 2 && `Abbiamo inviato un codice ${sentVia.includes('email') && sentVia.includes('sms') ? 'via email e SMS' : sentVia.includes('email') ? 'alla tua email' : 'al tuo telefono'}`}
              {step === 3 && 'Scegli una nuova password per il tuo account'}
            </p>
          </div>

          {/* Step 1: Request OTP */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Method Toggle */}
              <div className="flex rounded-lg overflow-hidden" style={{border: '1px solid #E5E7EB'}}>
                <button
                  type="button"
                  onClick={() => setMethod('email')}
                  className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    method === 'email' ? 'text-white' : ''
                  }`}
                  style={method === 'email' ? {background: '#2A9FCB'} : {color: '#2A9FCB'}}
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setMethod('phone')}
                  className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    method === 'phone' ? 'text-white' : ''
                  }`}
                  style={method === 'phone' ? {background: '#2A9FCB'} : {color: '#2A9FCB'}}
                >
                  <Phone className="w-4 h-4" />
                  Telefono
                </button>
              </div>

              {/* Input */}
              {method === 'email' ? (
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      placeholder="nome@esempio.com"
                      data-testid="reset-email-input"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Numero di telefono</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                      style={{borderColor: '#E5E7EB'}}
                      placeholder="+39 333 1234567"
                      data-testid="reset-phone-input"
                    />
                  </div>
                </div>
              )}

              <button
                onClick={requestOTP}
                disabled={loading}
                className="w-full py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                style={{background: loading ? '#9EC5B5' : 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}
                data-testid="send-otp-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Invio in corso...
                  </>
                ) : (
                  'Invia codice di verifica'
                )}
              </button>
            </div>
          )}

          {/* Step 2: Verify OTP */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Codice OTP (6 cifre)</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.replace(/\D/g, '').slice(0, 6)})}
                  className="w-full px-4 py-4 border rounded-lg focus:outline-none focus:ring-2 text-center text-2xl font-mono tracking-widest"
                  style={{borderColor: '#E5E7EB', letterSpacing: '0.5em'}}
                  placeholder="000000"
                  maxLength={6}
                  data-testid="otp-input"
                />
              </div>

              <button
                onClick={verifyOTP}
                disabled={loading || formData.code.length !== 6}
                className="w-full py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                style={{background: loading || formData.code.length !== 6 ? '#9EC5B5' : 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}
                data-testid="verify-otp-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Verifica in corso...
                  </>
                ) : (
                  'Verifica codice'
                )}
              </button>

              <button
                onClick={() => {
                  setFormData({...formData, code: ''});
                  requestOTP();
                }}
                className="w-full py-2 text-sm font-medium hover:underline"
                style={{color: '#2A9FCB'}}
              >
                Non hai ricevuto il codice? Invia di nuovo
              </button>
            </div>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Nuova password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                  <input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                    style={{borderColor: '#E5E7EB'}}
                    placeholder="Minimo 6 caratteri"
                    data-testid="new-password-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{color: '#2A9FCB'}}>Conferma password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{color: '#2A9FCB'}} />
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className="w-full pl-12 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2"
                    style={{borderColor: '#E5E7EB'}}
                    placeholder="Ripeti la password"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              {/* Password match indicator */}
              {formData.newPassword && formData.confirmPassword && (
                <div className={`flex items-center gap-2 text-sm ${
                  formData.newPassword === formData.confirmPassword ? 'text-green-600' : 'text-red-500'
                }`}>
                  {formData.newPassword === formData.confirmPassword ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Le password corrispondono
                    </>
                  ) : (
                    <>
                      <span className="text-red-500">✗</span>
                      Le password non corrispondono
                    </>
                  )}
                </div>
              )}

              <button
                onClick={resetPassword}
                disabled={loading || !formData.newPassword || formData.newPassword !== formData.confirmPassword}
                className="w-full py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                style={{background: loading || !formData.newPassword || formData.newPassword !== formData.confirmPassword 
                  ? '#9EC5B5' 
                  : 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)'}}
                data-testid="reset-password-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  'Reimposta password'
                )}
              </button>
            </div>
          )}

          {/* Progress Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-2 h-2 rounded-full transition-all ${
                  s === step ? 'w-6' : ''
                }`}
                style={{
                  background: s <= step ? '#2A9FCB' : '#E5E7EB'
                }}
              />
            ))}
          </div>
        </div>

        {/* Help text */}
        <p className="text-center text-sm mt-6" style={{color: '#2A9FCB', opacity: 0.7}}>
          Hai bisogno di aiuto? <a href="tel:+390810000000" className="font-medium hover:underline">Contattaci</a>
        </p>
      </div>
    </div>
  );
};

export default ForgotPassword;
